import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AlertCircle, CheckCircle, MessageSquare } from 'lucide-react';

const PublicSurveyPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  
  // Form states
  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [answers, setAnswers] = useState({});
  
  // Response token para controle de resposta única
  const [responseToken, setResponseToken] = useState(null);

  useEffect(() => {
    loadSurvey();
    
    // Verificar se já respondeu
    const storedToken = localStorage.getItem(`survey_response_${token}`);
    if (storedToken) {
      setResponseToken(storedToken);
      setCompleted(true);
    }
  }, [token]);

  const loadSurvey = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/surveys/public-link/${token}`);
      setSurvey(response.data.survey);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar pesquisa:', error);
      setError(error.response?.data?.detail || 'Erro ao carregar pesquisa');
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const handleCheckboxChange = (questionId, option, checked) => {
    const currentAnswers = answers[questionId] || [];
    let updated;
    
    if (checked) {
      updated = [...currentAnswers, option];
    } else {
      updated = currentAnswers.filter(o => o !== option);
    }
    
    setAnswers({
      ...answers,
      [questionId]: updated
    });
  };

  const validateAnswers = () => {
    const requiredQuestions = survey.questions.filter(q => q.required);
    
    for (const question of requiredQuestions) {
      const answer = answers[question.id];
      
      if (!answer || 
          (Array.isArray(answer) && answer.length === 0) ||
          (typeof answer === 'string' && answer.trim() === '')) {
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAnswers()) {
      alert('Por favor, responda todas as perguntas obrigatórias');
      return;
    }

    try {
      setSubmitting(true);
      
      // Criar array de respostas
      const answersArray = survey.questions.map(q => {
        const answer = answers[q.id];
        const answerObj = {
          question_id: q.id
        };

        if (q.question_type === 'likert') {
          answerObj.likert_score = parseInt(answer);
        } else if (q.question_type === 'checkbox') {
          answerObj.selected_options = answer || [];
        } else if (q.question_type === 'multiple_choice') {
          answerObj.selected_options = [answer];
        } else {
          answerObj.answer = answer;
        }

        return answerObj;
      });

      // Gerar ou usar token de resposta existente
      const newResponseToken = responseToken || `resp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const payload = {
        respondent_name: respondentName,
        respondent_email: respondentEmail,
        answers: answersArray,
        response_token: newResponseToken
      };

      await axios.post(`${backendUrl}/api/surveys/public-link/${token}/submit`, payload);

      // Salvar token no localStorage para prevenir múltiplas respostas
      localStorage.setItem(`survey_response_${token}`, newResponseToken);
      
      setCompleted(true);
      setResponseToken(newResponseToken);

    } catch (error) {
      console.error('Erro ao enviar respostas:', error);
      alert(error.response?.data?.detail || 'Erro ao enviar respostas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="h-16 w-16 mx-auto mb-4 text-emerald-600 animate-pulse" />
          <p className="text-gray-600">Carregando pesquisa...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Erro ao Carregar Pesquisa</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-emerald-600" />
            <h2 className="text-2xl font-bold mb-2">Obrigado!</h2>
            <p className="text-gray-600">
              Sua resposta foi registrada com sucesso. Agradecemos sua participação!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <CardTitle className="text-2xl">{survey.title}</CardTitle>
            {survey.description && (
              <p className="text-emerald-50 mt-2">{survey.description}</p>
            )}
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados do Respondente */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h3 className="font-semibold text-lg mb-4">Seus Dados</h3>
              <div>
                <label className="block text-sm font-medium mb-2">Nome (opcional)</label>
                <input
                  type="text"
                  value={respondentName}
                  onChange={(e) => setRespondentName(e.target.value)}
                  className="w-full p-3 border rounded"
                  placeholder="Seu nome"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email (opcional)</label>
                <input
                  type="email"
                  value={respondentEmail}
                  onChange={(e) => setRespondentEmail(e.target.value)}
                  className="w-full p-3 border rounded"
                  placeholder="seu@email.com"
                />
              </div>
            </CardContent>
          </Card>

          {/* Perguntas */}
          {survey.questions.map((question, index) => (
            <Card key={question.id} className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <p className="font-semibold mb-2">
                    {index + 1}. {question.question_text}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </p>
                </div>

                {/* Multiple Choice */}
                {question.question_type === 'multiple_choice' && (
                  <div className="space-y-2">
                    {question.options.map((option, optIndex) => (
                      <label key={optIndex} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="radio"
                          name={question.id}
                          value={option}
                          checked={answers[question.id] === option}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          className="w-4 h-4"
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Checkbox */}
                {question.question_type === 'checkbox' && (
                  <div className="space-y-2">
                    {question.options.map((option, optIndex) => (
                      <label key={optIndex} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={(answers[question.id] || []).includes(option)}
                          onChange={(e) => handleCheckboxChange(question.id, option, e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Short Text */}
                {question.question_type === 'short_text' && (
                  <input
                    type="text"
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="w-full p-3 border rounded"
                    placeholder="Sua resposta"
                  />
                )}

                {/* Long Text */}
                {question.question_type === 'long_text' && (
                  <textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="w-full p-3 border rounded"
                    rows={4}
                    placeholder="Sua resposta"
                  />
                )}

                {/* Likert Scale */}
                {question.question_type === 'likert' && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                      <span>1 = Discordo Totalmente</span>
                      <span>5 = Concordo Totalmente</span>
                    </div>
                    <div className="flex gap-4 justify-center">
                      {[1, 2, 3, 4, 5].map(score => (
                        <button
                          key={score}
                          type="button"
                          onClick={() => handleAnswerChange(question.id, score)}
                          className={`w-12 h-12 rounded-full border-2 transition-colors ${
                            answers[question.id] === score
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white hover:bg-gray-50 border-gray-300'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Yes/No */}
                {question.question_type === 'yes_no' && (
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => handleAnswerChange(question.id, 'Sim')}
                      className={`flex-1 py-3 rounded border-2 transition-colors ${
                        answers[question.id] === 'Sim'
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAnswerChange(question.id, 'Não')}
                      className={`flex-1 py-3 rounded border-2 transition-colors ${
                        answers[question.id] === 'Não'
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      Não
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            size="lg"
          >
            {submitting ? 'Enviando...' : 'Enviar Respostas'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PublicSurveyPage;
